'use client';

import { Toast } from '@florencelabs/toast';
import clsx from 'clsx';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { sortLocationsForDetails } from '@provider-directory/functions/sort-locations';
import {
  shouldContactProviderForInsurance,
  shouldHideAcceptedInsurances,
} from '@provider-directory/models/insurance.model';
import { getProviderPrimarySpecialty } from '@provider-directory/models/provider.model';
import { ProviderDetailsResource } from '@provider-directory/types/providers.contracts';
import ProviderTextSection from '../../provider-text-section/provider-text-section.component';
import { InsuranceList } from '../insurance/insurance-list.component';
import { ProviderDetailLinks } from '../links/links.component';
import { ProviderDetailsLocation } from '../location/location.component';
import { ProviderDetailSideMenu } from '../side-menu/side-menu.component';

interface ProviderDetailsProps {
  provider: ProviderDetailsResource;
}

export const ProviderDetailSections: FC<ProviderDetailsProps> = ({
  provider,
}) => {
  const { t } = useTranslation();
  const { runtimeConfig } = useOrgContext();

  const primarySpecialty = getProviderPrimarySpecialty(provider);
  const isInsurancesHidden = shouldHideAcceptedInsurances(primarySpecialty);

  const credentialItems = [
    {
      title: t('details.educationAndTraining'),
      content: [
        {
          title: t('details.medicalEducation'),
          content: provider.medicalEducation.join(', '),
        },
        {
          title: t('details.internship'),
          content: provider.internship ?? '',
        },
        {
          title: t('details.residency'),
          content: provider.residencies.length > 0 ? provider.residencies : '',
        },
        {
          title: t('details.fellowship'),
          content: provider.fellowships.length > 0 ? provider.fellowships : '',
        },
      ].filter((item) => item.content),
    },
    {
      title: t('details.boardCertification'),
      content: provider.boardCertifications?.filter(Boolean) ?? [],
    },
    {
      title: t('details.titlesAndPositions'),
      content: provider.organizationTitles ?? [],
    },
  ].filter((item) => item.content.length);

  const expertiseItems = [
    {
      title: t('details.focus'),
      content: [
        {
          title: t('details.clinicalFocus'),
          content: provider.clinicalFocus || '',
        },
        {
          title: t('details.researchFocus'),
          content: provider.researchFocus || '',
        },
      ].filter((item) => item.content),
    },
    {
      title: t('details.specialties'),
      content: provider.specialties.map(({ name }) => name).filter(Boolean),
    },
    {
      title: t('details.conditionsTreated'),
      content: provider.conditions?.filter(Boolean) ?? [],
    },
  ].filter((item) => item.content.length);

  const shouldShowContactProviderForInsurance =
    shouldContactProviderForInsurance(
      provider,
      runtimeConfig.contactProviderForInsuranceRule,
    );

  return (
    <div
      data-testid={`provider-${provider.npi}-details`}
      className={'w-full bg-white'}
    >
      <div
        className={clsx([
          'outer-margin pb-6 tablet:pb-10 desktop:pt-10',
          'grid grid-cols-[1fr] desktop:grid-cols-[1fr_3fr_392px]',
        ])}
      >
        <ProviderDetailSideMenu provider={provider} />
        <div className={'flex flex-col gap-8 pt-10 desktop:pt-0'}>
          {provider.biography && (
            <ProviderTextSection
              id={'about'}
              title={t('details.about')}
              content={provider.biography}
            />
          )}
          <ProviderTextSection
            id={'credentials'}
            title={t('details.credentials')}
            content={credentialItems}
          />
          <ProviderTextSection
            id={'expertise'}
            title={t('details.expertise')}
            content={expertiseItems}
          />
          <div
            className={'flex flex-col gap-6 pb-6'}
            id={'insurance'}
            data-testid={`provider-${provider.npi}-section-insurance`}
          >
            <h2
              className={
                'font-serif text-display-large text-gray-900 desktop:text-display-xlarge'
              }
            >
              {t('details.acceptedInsurance')}
            </h2>
            {shouldShowContactProviderForInsurance ? (
              <p className={'text-body-medium'}>
                {t('details.contactProviderForInsurance')}
              </p>
            ) : isInsurancesHidden ? (
              <p className={'text-body-medium'}>
                {t('schedule.insuranceInfo.contactProviderForInsurance')}
              </p>
            ) : (
              <>
                <Toast
                  variant={'warning'}
                  layout={'multiLine'}
                  isDismissible={false}
                  description={t('details.insuranceEligibility')}
                  cornerRadius={'medium'}
                />
                <InsuranceList
                  hospitalAssociations={provider.hospitalAssociations}
                  specialties={provider.specialties}
                  status={provider.status}
                />
              </>
            )}
          </div>
          {provider.locations.length > 0 && (
            <div
              className={'flex flex-col gap-4 pb-6'}
              id={'locations'}
              data-testid={`provider-${provider.npi}-section-locations`}
            >
              <h2
                className={
                  'font-serif text-display-large text-gray-900 desktop:text-display-xlarge'
                }
              >
                {t('details.locations')}
              </h2>
              <ul
                className={
                  'grid gap-4 mobile:grid-cols-[1fr] tablet:grid-cols-[1fr_1fr] desktop:grid-cols-[1fr]'
                }
              >
                {sortLocationsForDetails(provider.locations).map((location) => (
                  <ProviderDetailsLocation
                    key={location.id}
                    provider={provider}
                    {...location}
                  />
                ))}
              </ul>
            </div>
          )}
          <ProviderDetailLinks provider={provider} />
        </div>
      </div>
    </div>
  );
};

export default ProviderDetailSections;
